import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Input, Button, Flex, Skeleton, ConfigProvider } from 'antd';
import { useParams } from 'react-router';
import { message } from 'antd';

import {
  setCardProps as setCardPropsStore,
  relinkFeedCard as relinkFeedCardStore,
  unlinkFeedCard,
  setLeftSideTabState,
  highlightCard,
} from '../../actions';
import {
  TranscriptItemInput,
  RelinkFeedCardInput,
  RelinkFeedCardMutation,
} from '../../graphql/api';
import { relinkFeedCard } from '../../graphql/mutations';
import useMutation from '../../hooks/useMutation';
import useRecording from '../../hooks/useQuery/useRecording';
import SpaceItem from './SpaceItem';
import WordItem from './WordItem';
import { IState } from '../../graphql/types';
import { GiideRouteParams } from '../../types';
import styles from '../Components.module.css';
import useTranscript from '../../hooks/useTranscript';
import { WarningOutlined } from '@ant-design/icons';

const SelectableArea: React.FC<{
  recordId: string;
  isInLibrary?: boolean;
  editingTranscript: boolean;
}> = ({ recordId, isInLibrary, editingTranscript }) => {
  const dispatch = useDispatch();
  const { id: giideId } = useParams<GiideRouteParams>();
  const { recording } = useRecording(recordId);

  const {
    data: transcriptData,
    loading,
    refetch,
    updateWord,
    saving,
  } = useTranscript(giideId, recordId);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const [editModalVisible, setEditModalVisible] =
    React.useState<boolean>(false);
  const [editWordId, setEditWordId] = React.useState<string | undefined>(
    undefined
  );
  const [newWord, setNewWord] = React.useState<string>('');

  const activeWordInTranscribe = useSelector<
    IState,
    TranscriptItemInput | undefined
  >(state => state.activeWordInTranscribe);

  const playing = useSelector<IState, boolean>(
    state => !!state.player?.playing
  );
  const addFeedcardModalOpened = useSelector<IState, boolean>(
    state => !!state.addFeedcardModalOpened
  );
  const lastWord = recording?.transcriptResults?.transcript?.split(' ').pop();

  const relinkFeedCardMutation = useMutation<
    RelinkFeedCardInput,
    RelinkFeedCardMutation
  >(relinkFeedCard);
  const relinkingFeedcard = useSelector<IState, string | undefined | null>(
    state => state.relinkingFeedcard
  );

  const cardTimes = React.useMemo<string[]>(
    () =>
      recording
        ? recording.feedCards?.items
            ?.filter(f => !f?.unlinked)
            .map(item => item!.time.toString()) || []
        : [],
    [recording]
  );

  const setCardProps = React.useCallback(
    async (selectedWord?: string, startTime?: string | null) => {
      dispatch(setLeftSideTabState('feed'));
      if (relinkingFeedcard) {
        const time = parseFloat(startTime || '');
        if (!recording?.id || !relinkingFeedcard || !time) {
          return;
        }

        const originalCard = recording?.feedCards?.items?.find(
          card => card?.id === relinkingFeedcard
        );

        if (!originalCard || originalCard['recording']['id'] !== recording.id) {
          message.error('Cannot link feedcard to different chapter.');
          return;
        }

        dispatch(relinkFeedCardStore(recording?.id, relinkingFeedcard, time));
        try {
          const result = await relinkFeedCardMutation.callMutation({
            id: relinkingFeedcard,
            recordingId: recording.id,
            time: time,
          });
          if (result.data.relinkFeedCard?.success) {
            message.success('Feedcard successfully linked.');
          } else {
            throw new Error('An error occured.');
          }
        } catch (err) {
          message.error('Error while linking feedcard.');
          dispatch(
            unlinkFeedCard(
              recording.id,
              relinkingFeedcard,
              originalCard?.time || 0
            )
          );
        }
      } else if (selectedWord && startTime && !cardTimes.includes(startTime)) {
        dispatch(
          setCardPropsStore({
            time: parseFloat(startTime || ''),
            recording,
            selectedWord: selectedWord || '',
            type: undefined,
            isInLibrary,
          })
        );
      }
    },
    [
      recording,
      cardTimes,
      dispatch,
      isInLibrary,
      relinkingFeedcard,
      relinkFeedCardMutation,
    ]
  );

  const onClickWord = React.useCallback(
    (index: number, punctuation?: boolean, cardID?: string) => () => {
      if (cardID) {
        dispatch(highlightCard(cardID));
      }

      cardID
        ? document.getElementById(cardID)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        : punctuation
        ? setCardProps(
            recording?.transcriptResults?.items[index + 1]?.alternatives[0]
              ?.content,
            recording?.transcriptResults?.items[index + 1]?.start_time
          )
        : setCardProps(
            recording?.transcriptResults?.items[index]?.alternatives[0]
              ?.content,
            recording?.transcriptResults?.items[index]?.start_time
          );
    },
    [recording, setCardProps, dispatch]
  );

  const handleCloseModal = React.useCallback(() => {
    setEditWordId(undefined);
    setNewWord('');
    setEditModalVisible(false);
  }, []);

  const onEditWord = React.useCallback(() => {
    if (!editWordId) return;
    updateWord(giideId, recordId, editWordId, newWord, handleCloseModal);
  }, [editWordId, newWord, updateWord, giideId, recordId, handleCloseModal]);

  const renderItems = React.useMemo<JSX.Element[] | undefined>(
    () =>
      (transcriptData as TranscriptItemInput[] | undefined)?.map(
        (item, index, allItems) => {
          const textToRender = item?.alternatives?.[0]?.content;
          const isNextPunctuation = allItems[index + 1]?.type === 'punctuation';

          let nextWordStartTime = Infinity;
          for (let i = index + 1; i < allItems.length; i++) {
            const currentItem = allItems[i];
            if (currentItem && currentItem.type !== 'punctuation') {
              const startTime = currentItem.start_time;

              if (typeof startTime !== 'string') {
                continue;
              }

              nextWordStartTime = parseFloat(startTime);

              break;
            }
          }

          let prevWordStartTime = Infinity;
          for (let i = index - 1; i >= 0; i--) {
            const currentItem = allItems[i];
            if (currentItem && currentItem.type !== 'punctuation') {
              const startTime = currentItem.start_time;

              if (typeof startTime !== 'string') {
                continue;
              }

              prevWordStartTime = parseFloat(startTime);

              break;
            }
          }

          const thisWordStartTime = item.start_time
            ? parseFloat(item.start_time)
            : -Infinity;

          const matchingFeedCardIdx = cardTimes.findIndex(cardTime => {
            if (!cardTime) {
              return false;
            }

            const thisCardTime = parseFloat(cardTime as string);

            const deltaCurrent = Math.abs(thisCardTime - thisWordStartTime);
            const deltaPrevious = Math.abs(thisCardTime - prevWordStartTime);
            const deltaNext = Math.abs(thisCardTime - nextWordStartTime);

            return deltaCurrent < deltaPrevious && deltaCurrent < deltaNext;
          });

          const isHighlighted = !!item.start_time && matchingFeedCardIdx > -1;

          const isActive =
            item.start_time === activeWordInTranscribe?.start_time &&
            !(
              activeWordInTranscribe &&
              lastWord?.includes(
                activeWordInTranscribe.alternatives[0].content
              ) &&
              !playing
            );

          const cardID = recording?.feedCards?.items[matchingFeedCardIdx]?.id;

          const onClick = editingTranscript
            ? () => {
                if (item.type === 'punctuation') return;
                setEditWordId(item.transcriptItemID!);
                setNewWord(textToRender);
                setEditModalVisible(true);
              }
            : onClickWord(index, item.type === 'punctuation', cardID);

          const space = (
            <SpaceItem
              key={`space${index}`}
              text={textToRender}
              pronunciation={item.type === 'pronunciation'}
              onClick={onClick}
            />
          );

          return item.type === 'punctuation' ? (
            space
          ) : (
            <React.Fragment key={index}>
              <WordItem
                text={textToRender}
                key={index}
                isHighlighted={isHighlighted}
                onClick={onClick}
                cardId={cardID}
                recordingId={recording?.id}
                startTime={item.start_time}
                isActive={isActive}
                disabled={addFeedcardModalOpened}
                editingTranscript={editingTranscript}
                isEditingWord={editWordId === item.transcriptItemID}
              />
              {!isNextPunctuation && space}
            </React.Fragment>
          );
        }
      ),
    [
      cardTimes,
      onClickWord,
      recording,
      activeWordInTranscribe,
      playing,
      lastWord,
      addFeedcardModalOpened,
      transcriptData,
      editingTranscript,
      editWordId,
    ]
  );

  return (
    <div className={styles.selectAreaWrapper}>
      <Modal
        title={'Edit Word'}
        destroyOnClose
        centered
        closable={false}
        onCancel={handleCloseModal}
        open={editModalVisible}
        onOk={onEditWord}
        footer={
          <Flex justify={'flex-end'}>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button
              type={'primary'}
              disabled={newWord.length === 0}
              onClick={onEditWord}
              loading={saving}
            >
              Save
            </Button>
          </Flex>
        }
      >
        <Input
          value={newWord}
          maxLength={50}
          onChange={e => {
            if (
              e.target.value.match(/^$|[0-9A-Za-zÀ-ÖØ-öø-ÿ-'']+$/) &&
              !e.target.value.match(/--/)
            ) {
              setNewWord(e.target.value);
            } else return;
          }}
        />
      </Modal>

      <div
        className="select-area"
        style={
          editingTranscript
            ? { color: '#202e38', backgroundColor: '#F7F7F7' }
            : {}
        }
      >
        <ConfigProvider theme={{ token: { colorFillContent: '#344049' } }}>
          <Skeleton paragraph={{ rows: 6 }} loading={loading}>
            {!transcriptData && (
              <div className={styles.emptyTranscriptWarning}>
                <WarningOutlined /> Unable to detect any words in the selected
                audio file. To create and attach feed cards to your audio,
                please ensure the selected audio contains spoken words.
              </div>
            )}
            {renderItems}
          </Skeleton>
        </ConfigProvider>
      </div>
    </div>
  );
};

export default SelectableArea;
